/**
 * CR204: Block Registry
 * CR228: Added DealTypeFilterBlock, AmbiguousDealsBlock, EmptyStateBlock
 * CR500: Added ResultsMetaBlock, DividerBlock - all UI must be blocks
 * 
 * Central registry for all layout block components.
 * Add new block types here to make them available in the LayoutRenderer.
 */

import type { ComponentType } from "react";
import type { LayoutBlockId } from "@/lib/layout-studio/types";
import type { BlockProps } from "./types";

import { HeaderBlock } from "./HeaderBlock";
import { SearchFiltersBlock } from "./SearchFiltersBlock";
import { SortBarBlock } from "./SortBarBlock";
import { DealTypeFilterBlock } from "./DealTypeFilterBlock";
import { ResultsMetaBlock } from "./ResultsMetaBlock";
import { DividerBlock } from "./DividerBlock";
import { ResultsBlock } from "./ResultsBlock";
import { AmbiguousDealsBlock } from "./AmbiguousDealsBlock";
import { EmptyStateBlock } from "./EmptyStateBlock";
import { FeaturedDealsBlock } from "./FeaturedDealsBlock";

// Export all block components
export { HeaderBlock } from "./HeaderBlock";
export { SearchFiltersBlock } from "./SearchFiltersBlock";
export { SortBarBlock } from "./SortBarBlock";
export { DealTypeFilterBlock } from "./DealTypeFilterBlock";
export { ResultsMetaBlock } from "./ResultsMetaBlock";
export { DividerBlock } from "./DividerBlock";
export { ResultsBlock } from "./ResultsBlock";
export { AmbiguousDealsBlock } from "./AmbiguousDealsBlock";
export { EmptyStateBlock } from "./EmptyStateBlock";
export { FeaturedDealsBlock } from "./FeaturedDealsBlock";

// Export types
export * from "./types";

/**
 * CR500: Block component registry - maps block IDs to their components.
 * This is the SINGLE SOURCE OF TRUTH for block → component mapping.
 * 
 * IMPORTANT: Every LayoutBlockId MUST have an entry here.
 * If you add a new block type, you MUST add it to this registry.
 */
export const BLOCK_REGISTRY: Record<LayoutBlockId, ComponentType<BlockProps<any>>> = {
  header: HeaderBlock,
  search_filters: SearchFiltersBlock,
  sort_bar: SortBarBlock,
  deal_type_filter: DealTypeFilterBlock,
  results_meta: ResultsMetaBlock,      // CR500
  divider: DividerBlock,               // CR500
  results: ResultsBlock,
  ambiguous_deals: AmbiguousDealsBlock,
  empty_state: EmptyStateBlock,
  featured_deals: FeaturedDealsBlock,
};

/**
 * Check if a block ID is registered
 */
export function isBlockRegistered(blockId: string): blockId is LayoutBlockId {
  return blockId in BLOCK_REGISTRY;
}
